// Tenant-currency context for POS production pages/components.
//
// Single source of truth for "what currency is this tenant on?" — read from
// the same auth/session store the rest of the POS uses for `business_type`
// (`useAuthStore().profile.tenant`). Currency is immutable per tenant
// (mirrors `business_type`), so a plain store read is sufficient — no FX,
// no conversion. Defaults to `'INR'` when the profile is not yet loaded or
// the value is missing/unknown, keeping every INR call site byte-identical.

import { useAuthStore } from '@/stores/use-auth-store';
import { formatCurrency } from '@/lib/format';
import { SUPPORTED_CURRENCIES } from '@bbpos/types';
import type { CurrencyCode } from '@bbpos/types';

/**
 * Narrow an arbitrary string (e.g. `tenant.default_currency`, typed as
 * `string` on the API contract) to a supported {@link CurrencyCode}.
 * Unknown / empty / legacy values fall back to `'INR'` — the correct default
 * for every existing tenant.
 */
export function toCurrencyCode(value: string | null | undefined): CurrencyCode {
  const code = (value ?? '').toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code)
    ? (code as CurrencyCode)
    : 'INR';
}

/**
 * Hook: the current tenant's immutable display currency, sourced from the
 * auth profile. Selector returns a primitive `CurrencyCode`, so it is stable
 * under Zustand's `Object.is` comparison and never re-renders spuriously.
 */
export function useTenantCurrency(): CurrencyCode {
  return useAuthStore((s) => toCurrencyCode(s.profile?.tenant?.default_currency));
}

/**
 * The display symbol for a currency, derived from the single shared formatter
 * rather than a duplicated symbol table. `formatCurrency(0, currency)` is
 * `symbol + zeroMajorString`; the major part is digits + locale separators
 * only, so stripping the trailing numeric run yields exactly the symbol used
 * everywhere else (`₹` for INR, ISO-code fallback for the GCC set).
 */
export function getCurrencySymbol(currency: CurrencyCode): string {
  return formatCurrency(0, currency).replace(/[\d.,\s]+$/u, '');
}
